import { ComponentType } from '@angular/cdk/portal';
import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  ActiveErrorResponse,
  DashboardEditColumnPayload,
  ErrorDatabaseResponse,
  DashboardColumn,
  DashboardInstallationData,
  SortOrder,
  ViError,
  GetColumnInstallationsPayload,
  DashboardColumnDataWithoutInstallationDetails,
} from '@vi-iot/client';
import {
  DashboardFilter,
  DashboardListFilterButtonData,
  DashboardRefreshModalData,
} from './kanban-board.model';
import {
  DashboardInstallationColumn,
  SingleDashboardColumnInstallationsUpdate,
} from './kanban-board.state';
import { Platform } from '../installation-details';

export namespace KanbanBoardActions {
  export const columnDialogActions = createActionGroup({
    source: 'KANBAN BOARD Column dialog',
    events: {
      open: props<{ modal: ComponentType<unknown>; column?: DashboardInstallationColumn }>(),
      close: emptyProps(),
    },
  });

  export const getDashboard = createActionGroup({
    source: 'KANBAN BOARD Get dashboard',
    events: {
      attempt: emptyProps(),
      success: props<{ columns: DashboardColumn[] }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const getFilters = createActionGroup({
    source: 'KANBAN BOARD Get filters',
    events: {
      attempt: emptyProps(),
      success: props<{ filters: DashboardFilter[] }>(),
      error: props<{ error: ViError }>(),
      aborted: props<{ error: string }>(),
    },
  });

  export const predefineDashboardFilter = createActionGroup({
    source: 'KANBAN BOARD Predefine dashboard filter',
    events: {
      set: props<{
        filters: DashboardFilter[];
      }>(),
      clear: emptyProps(),
    },
  });

  export const setDashboardFilter = createActionGroup({
    source: 'KANBAN BOARD Set dashboard filter',
    events: {
      attempt: emptyProps(),
      success: props<{
        filters: DashboardFilter[];
      }>(),
      aborted: emptyProps(),
    },
  });

  export const fetchKanbanDashboardWithFilters = createActionGroup({
    source: 'KANBAN BOARD Fetch kanban dashboard with filters',
    events: {
      attempt: emptyProps(),
      success: props<{ columns: DashboardColumn[] }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const addColumn = createActionGroup({
    source: 'KANBAN BOARD Add column',
    events: {
      attempt: props<{ name: string; sortOrder: SortOrder }>(),
      success: props<{ column: DashboardColumn }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const deleteColumn = createActionGroup({
    source: 'KANBAN BOARD Delete column',
    events: {
      attempt: props<{ id: number; version: string }>(),
      success: props<{ id: number }>(),
      error: props<{ error: ViError }>(),
      'error with retry attempt and data fetching': props<{
        id: number;
        version: string;
        error: ViError;
        errorLabel: string;
      }>(),
      retry: props<{ id: number; version: string }>(),
      'retry aborted': props<{ id: number; version: string }>(),
    },
  });

  export const editColumnName = createActionGroup({
    source: 'KANBAN BOARD Edit column name',
    events: {
      attempt: props<{
        columnId: number;
        data: DashboardEditColumnPayload;
      }>(),
      success: props<{ id: number; editColumnPayload: DashboardEditColumnPayload }>(),
      error: props<{ id: number; error: ViError }>(),
      'error with retry attempt and data fetching': props<{
        columnId: number;
        data: DashboardEditColumnPayload;
        error: ViError;
        errorLabel: string;
      }>(),
      retry: props<{
        columnId: number;
        data: DashboardEditColumnPayload;
      }>(),
      'retry aborted': props<{ columnId: number; error: ViError }>(),
    },
  });

  export const setColumnSortOrder = createActionGroup({
    source: 'KANBAN BOARD Set column sorting',
    events: {
      attempt: props<{ column: DashboardColumn; sortOrder: SortOrder }>(),
      success: props<{
        columnId: number;
        sortOrder: SortOrder;
        installations: DashboardInstallationData[];
      }>(),
      error: props<{ error: ViError }>(),
      'error with retry attempt and data fetching': props<{
        column: DashboardColumn;
        sortOrder: SortOrder;
        error: ViError;
        errorLabel: string;
      }>(),
      retry: props<{
        column: DashboardColumn;
        sortOrder: SortOrder;
      }>(),
      'retry aborted': props<{ error: ViError }>(),
    },
  });

  export const getInstallationSmartNotes = createActionGroup({
    source: 'KANBAN BOARD Get installation smart notes',
    events: {
      attempt: props<{ installationId: number }>(),
      success: props<{
        activeErrors: ActiveErrorResponse[];
      }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const getQuickViewErrorDetails = createActionGroup({
    source: 'KANBAN BOARD Get quick view error details',
    events: {
      attempt: props<{
        errorCode: string;
        countryCode: string;
        langCode: string;
        serial: string;
        smartNoteId: number;
      }>(),
      success: props<{
        smartNote: ErrorDatabaseResponse;
        smartNoteId: number;
      }>(),
      error: props<{ error: ViError; smartNoteId: number }>(),
    },
  });

  export const addTempColumn = createAction('[KANBAN BOARD] Add temporary column');

  export const deleteTempColumn = createAction('[KANBAN BOARD] Delete temporary column');

  export const manageColumnNameInput = createAction(
    '[KANBAN BOARD] Manage column name input',
    props<{ columnId: number; inputShowed: boolean }>()
  );

  export const openQuickViewPanel = createAction(
    '[KANBAN BOARD] Open quick view panel',
    props<{ installation: DashboardInstallationData; deviceIndex: number }>()
  );

  export const closeQuickViewPanel = createAction('[KANBAN BOARD] Close quick view panel');

  export const changeQuickViewDeviceIndex = createAction(
    '[KANBAN BOARD] Change quick view device id',
    props<{ deviceIndex: number }>()
  );

  export const fetchInstallationsForColumn = createActionGroup({
    source: 'KANBAN BOARD Fetch installations for column',
    events: {
      // types not known yet, mocks done blindly
      attempt: props<{ payload: GetColumnInstallationsPayload }>(),
      success: props<{ columnId: number; installations: DashboardInstallationData[] }>(),
      error: props<{ columnId: number; error: ViError }>(),
    },
  });

  export const copyKanbanInstallation = createActionGroup({
    source: 'KANBAN BOARD Copy kanban installation',
    events: {
      attempt: props<{
        installation: DashboardInstallationData;
        previousColumnId: number;
        nextColumnId: number;
      }>(),
      success: props<{ columnId: number; version: string }>(),
      error: props<{
        installation: DashboardInstallationData;
        nextColumnId: number;
        error: ViError;
      }>(),
    },
  });

  export const moveKanbanInstallation = createActionGroup({
    source: 'KANBAN BOARD Move kanban installation',
    events: {
      attempt: props<{
        installation: DashboardInstallationData;
        previousColumnId: number;
        nextColumnId: number;
      }>(),
      success: props<{
        columnsToUpdate: [
          DashboardColumnDataWithoutInstallationDetails,
          DashboardColumnDataWithoutInstallationDetails
        ];
      }>(),
      error: props<{
        installation: DashboardInstallationData;
        previousColumnId: number;
        nextColumnId: number;
        error: ViError;
      }>(),
    },
  });

  export const moveKanbanColumn = createActionGroup({
    source: 'KANBAN BOARD Move kanban column',
    events: {
      attempt: props<{
        movedColumnIndex: number;
        movedColumnId: number;
        targetColumnIndex: number;
        targetColumnId: number;
      }>(),
      success: props<{ columnId: number; version: string }>(),
      error: props<{ error: ViError; movedColumnIndex: number; targetColumnIndex: number }>(),
    },
  });

  export const updateColumnVersions = createActionGroup({
    source: 'KANBAN BOARD Update column versions',
    events: {
      attempt: props<{ payload: GetColumnInstallationsPayload }>(),
      success: props<{
        columns: DashboardColumn[];
      }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const updateColumnsAndRefetchInstallations = createActionGroup({
    source: 'KANBAN BOARD Update columns and refetch installations',
    events: {
      attempt: emptyProps(),
      success: props<{
        dashboard: DashboardColumn[];
        columnsWithNewVersion: SingleDashboardColumnInstallationsUpdate[];
      }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const deleteKanbanInstallationCopy = createActionGroup({
    source: 'KANBAN BOARD Delete kanban installation copy',
    events: {
      attempt: props<{
        installationId: number;
        columnId: number;
      }>(),
      success: props<{ columnId: number; version: string }>(),
      error: props<{ error: ViError; columnId: number }>(),
    },
  });

  export const createNewColumnAndMoveInstallationToIt = createActionGroup({
    source: 'KANBAN BOARD Create new column and move installation to it',
    events: {
      'create input': props<{
        installation: DashboardInstallationData;
        previousColumnId: number;
      }>(),
      aborted: emptyProps(),
      'column creation attempt': props<{
        newColumnName: string;
      }>(),
      'column creation failed': props<{ error: ViError }>(),
      'column creation success': props<{
        column: DashboardColumn;
        installation: DashboardInstallationData;
      }>(),
      'move installation attempt': props<{
        installation: DashboardInstallationData;
        previousColumnId: number;
        nextColumnId: number;
      }>(),
      'move installation successful': props<{
        columnsToUpdate: [
          DashboardColumnDataWithoutInstallationDetails,
          DashboardColumnDataWithoutInstallationDetails
        ];
      }>(),
      'move installation failed': props<{
        installation: DashboardInstallationData;
        previousColumnId: number;
        nextColumnId: number;
        error: ViError;
      }>(),
    },
  });

  export const createNewColumnAndCopyInstallationToIt = createActionGroup({
    source: 'KANBAN BOARD Create new column and copy installation to it',
    events: {
      'create input': props<{
        installation: DashboardInstallationData;
      }>(),
      aborted: emptyProps(),
      'column creation attempt': props<{
        newColumnName: string;
      }>(),
      'column creation failed': props<{ error: ViError }>(),
      'column creation success': props<{
        column: DashboardColumn;
        installation: DashboardInstallationData;
      }>(),
    },
  });

  export const checkFilterButtonDataPresenceBeforeFetchingInstallations = createAction(
    '[KANBAN BOARD] Check filter button data presence before fetching installations'
  );

  export const initialFetchInstallationList = createActionGroup({
    source: 'KANBAN BOARD Initial fetch installation list',
    events: {
      attempt: emptyProps(),
      success: props<{
        installations: DashboardInstallationData[];
        cursor: string;
      }>(),
      error: props<{ error: ViError }>(),
      'snapshot outdated error': props<{ offset: number; limit: number }>(),
    },
  });

  export const retryInitialFetchInstallationList = createActionGroup({
    source: 'KANBAN BOARD Retry initial fetch installation list',
    events: {
      attempt: emptyProps(),
      success: props<{
        installations: DashboardInstallationData[];
        cursor: string;
      }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const getButtonFilterData = createActionGroup({
    source: 'KANBAN BOARD Get button filter data',
    events: {
      attempt: emptyProps(),
      success: props<{
        buttonFilterData: DashboardListFilterButtonData[];
      }>(),
      error: emptyProps(),
      'snapshot outdated error': emptyProps(),
      retry: emptyProps(),
    },
  });

  export const errorWhileFetchingSnapshotInListView = createAction(
    '[KANBAN BOARD] Error while fetching snapshot in list view',
    props<{ error: ViError }>()
  );

  export const nextFetchInstallationList = createActionGroup({
    source: 'KANBAN BOARD Next fetch installation list',
    events: {
      attempt: props<{ offset: number; limit: number }>(),
      success: props<{ installations: DashboardInstallationData[]; cursor: string }>(),
      error: props<{ error: ViError }>(),
      'snapshot outdated error': props<{ offset: number; limit: number }>(),
    },
  });

  export const retryNextFetchInstallationList = createActionGroup({
    source: 'KANBAN BOARD Retry next fetch installation list',
    events: {
      attempt: props<{ offset: number; limit: number }>(),
      success: props<{ installations: DashboardInstallationData[]; cursor: string }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const clearSelectedFilters = createAction('[KANBAN BOARD] Clear selected filters');

  export const applyFiltersByClickingButton = createAction(
    '[KANBAN BOARD] Apply filters by clicking button',
    props<{ selectedFilter: DashboardListFilterButtonData }>()
  );

  export const openForcedDashboardRefreshDialog = createAction(
    '[KANBAN BOARD] Open forced dashboard refresh dialog',
    props<{ modalData: DashboardRefreshModalData }>()
  );

  export const navigateToBooking = createAction(
    '[KANBAN BOARD] Navigate to booking',
    props<{ installation: DashboardInstallationData; platform: Platform }>()
  );

  export const refreshSnapshotInInstallationList = createActionGroup({
    source: '[KANBAN BOARD] Refresh snapshot in installation list',
    events: {
      attempt: emptyProps(),
      success: emptyProps(),
      error: emptyProps(),
    },
  });

  export const recreateSnapshot = createActionGroup({
    source: '[KANBAN BOARD] Recreate snapshot',
    events: {
      attempt: emptyProps(),
      success: emptyProps(),
      error: props<{ error: ViError }>(),
    },
  });
}
